<template>
  <div :class="_class">
    <label v-if="!inline && labelMenu" :for="id + '-menu-btn'">{{
      labelMenu
    }}</label>
    <inline-menu
      :label="criteriaText"
      :labelMenu="inline ? labelMenu : null"
      :menuButtonId="id + '-menu-btn'"
      :html="true"
      :button="!link"
      :secondary="!link"
      trigger-class="mln small"
      :class="menuClass"
      @ui-open="onOpenMenu"
      @ui-close="onCloseMenu"
    >
      <template #default="{ close }">
        <div class="ps">
          <div class="cat-menu-dropdown-body">
            <div class="plx prx pbt mbx">
              <cat-radio-buttons
                v-if="radio"
                :id="id + '-criteria'"
                :items="options"
                :selected="criteria"
                :label="labelMenu"
                :mandatory="true"
                class="pbn"
                @ui-change="updateDateCriteria"
              >
              </cat-radio-buttons>
              <cat-attribute-select
                v-else
                :id="id + '-criteria'"
                :label="labelMenu"
                :value="criteria"
                size="large"
                :mandatory="true"
                suppress-default-option
                :items="options"
                class="mbn mrn"
                @ui-change="updateDateCriteria"
              >
              </cat-attribute-select>
              <cat-attribute-date
                v-if="criteria !== ''"
                :id="id + '-date1'"
                :value="modelDate1"
                :errors="errorsDate1"
                :placeholder="labelDate1"
                :mandatory="true"
                class="mrn"
                :field-order="fieldOrder"
                @ui-change="updateDate($event, 0)"
              >
              </cat-attribute-date>
              <cat-attribute-date
                v-if="criteria === 'between'"
                :id="id + '-date2'"
                :value="modelDate2"
                :errors="errorsDate2"
                :placeholder="labelDate2"
                :mandatory="true"
                class="mrn"
                :field-order="fieldOrder"
                @ui-change="updateDate($event, 1)"
              >
              </cat-attribute-date>
              <div>
                <cat-btn
                  :link="true"
                  :small="true"
                  @ui-click="onCancel(close)"
                  >{{ labelCancel }}</cat-btn
                >
                <cat-btn
                  :hollow="true"
                  :link="true"
                  :right="true"
                  @ui-click="onConfirm(close)"
                  >{{ labelConfirm }}</cat-btn
                >
              </div>
            </div>
          </div>
          <div class="cat-menu-dropdown-footer"></div>
        </div>
      </template>
    </inline-menu>
  </div>
</template>
<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatAttributeSelect from "@/components/catAttributeSelect/catAttributeSelect.vue";
import CatAttributeDate from "@/components/catAttributeDate/catAttributeDate.vue";
import CatRadioButtons from "@/components/catRadioButtons/catRadioButtons.vue";
import InlineMenu from "@/components/catMenu/catMenu.vue";
import CatBtn from "@/components/catBtn/catBtn.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatDateRange",
  components: {
    CatAttributeSelect,
    CatAttributeDate,
    CatBtn,
    InlineMenu,
    CatRadioButtons
  },
  props: {
    fieldOrder: {
      type: String,
      description:
        "Applies to the date fields. Order of individual fields 'd','m' and 'y' separated by comma - . E.g. 'd,m,y'",
      validator: function (value) {
        let v = value.split(",").filter(String);
        return v.length > 0 && v.length < 4;
      },
      default: "d,m,y"
    },
    id: {
      type: [Number, String],
      required: true,
      description: "Date range ID."
    },
    criteriaOptions: {
      type: Array,
      description:
        "List of criteria option objects with fields -- value, text, label (this is used if using radio display). Default option values: '', equal, before, after, between."
    },
    customCriteriaText: {
      type: Boolean,
      default: true,
      description:
        "Flag to indicate if supplying the criteria text (see valueCriteriaText) to be displayed in the menu button."
    },
    valueCriteriaText: {
      type: String,
      default: "Any Date",
      description: "The text to be displayed in the menu button."
    },
    valueCriteria: { type: String, default: "", description: "desc" },
    valueDate1: { type: String, description: "desc" },
    valueDate2: { type: String, description: "desc" },
    link: {
      type: Boolean,
      default: false,
      description:
        "Displays the menu button as a link. If false, the menu button is displayed as secondary."
    },
    inline: {
      type: Boolean,
      default: false,
      description: "Displays the menu button as inline."
    },
    radio: {
      type: Boolean,
      default: false,
      description:
        "Indicates if the criteria is rendered as a radio group instead of drop-down."
    },
    labelCancel: {
      type: String,
      default: "Cancel",
      description: "Cancel button label."
    },
    labelConfirm: {
      type: String,
      default: "Confirm",
      description: "Confirm change button label."
    },
    labelMenu: {
      type: String,
      default: null,
      description: "Menu button label."
    },
    labelDate1: {
      type: String,
      default: "Date 1",
      description: "Date 1 label."
    },
    labelDate2: {
      type: String,
      default: "Date 2",
      description: "Date 2 label."
    },
    minDate: {
      type: String,
      default: "",
      description: "Earliest date that can be entered."
    },
    maxDate: {
      type: String,
      default: "",
      description: "Latest date that can be entered."
    },
    errorMsgMinMax: {
      type: String,
      default: "The date must be between the minimum and maximum.",
      description: "desc"
    }
  },
  data: function () {
    const options = this.criteriaOptions || [
      {
        value: "",
        text: this.resolveTxt("skin.dateRange.criteria.any", "Any date")
      },
      {
        value: "equal",
        label: this.resolveTxt(
          "skin.dateRange.criteria.equal",
          "Equals to the entered date"
        ),
        text: this.resolveTxt(
          "skin.dateRange.criteria.equal",
          "Equals to the entered date"
        )
      },
      {
        value: "before",
        label: this.resolveTxt(
          "skin.dateRange.criteria.before",
          "Before the entered date"
        ),
        text: this.resolveTxt(
          "skin.dateRange.criteria.before",
          "Before the entered date"
        )
      },
      {
        value: "after",
        label: this.resolveTxt(
          "skin.dateRange.criteria.after",
          "On or after the entered date"
        ),
        text: this.resolveTxt(
          "skin.dateRange.criteria.after",
          "On or after the entered date"
        )
      },
      {
        value: "between",
        label: this.resolveTxt(
          "skin.dateRange.criteria.after",
          "On or after the entered date"
        ),
        text: this.resolveTxt(
          "skin.dateRange.criteria.between",
          "Between two entered dates"
        )
      }
    ];
    let arr: any[] = [];
    let arr2: any[] = [];
    return {
      options: options,
      criteriaText: this.customCriteriaText
        ? this.valueCriteriaText
        : this.getCriteriaText(options, this.valueCriteria || ""),
      criteria: "",
      date1: "",
      date2: "",
      modelDate1: "", // value used by the catAttributeDate component
      modelDate2: "", // value used by the catAttributeDate component
      errorsDate1: arr,
      errorsDate2: arr2
    };
  },
  computed: {
    _class: function () {
      return {
        "cat-date-range-menu": true,
        "fieldset pbn mbn": !this.inline,
        inline: this.inline
      };
    },
    menuClass: function () {
      return {
        "pbn mln": !this.inline,
        mrt: this.inline
      };
    }
  },
  watch: {
    valueCriteriaText: function (newText) {
      if (this.customCriteriaText) {
        this.criteriaText = newText;
      }
    }
  },
  methods: {
    getCriteriaText: function (options, value) {
      var text = "";
      options.some(function (option) {
        if (option.value === value) {
          text = option.text;
          return true;
        }
      });
      return text;
    },
    dateString: function (date) {
      const year = ("0000" + date.getFullYear()).slice(-4);
      const month = ("00" + (date.getMonth() + 1)).slice(-2);
      const day = ("00" + date.getDate()).slice(-2);
      return year + "-" + month + "-" + day;
    },
    updateDateCriteria: function (e) {
      this.criteria = e.value;
      this.errorsDate1 = [];
      this.errorsDate2 = [];

      if (this.criteria !== "") {
        // Do not update the current date 1 field value when just changing the criteria
        if (!this.modelDate1 || this.modelDate1 === "") {
          // The date 1 is empty, set to the current date, date 2 no default as per spec.
          this.date1 = this.dateString(new Date());
          this.modelDate1 = this.date1;
        }

        // Reset date 2 if not between
        if (this.criteria !== "between") {
          this.date2 = "";
          this.modelDate2 = this.date2;
        }
      } else {
        // Reset the dates when Any date is selected
        this.date1 = "";
        this.date2 = "";
        this.modelDate1 = this.date1;
        this.modelDate2 = this.date2;
      }
    },
    updateDate: function (e, index) {
      if (index === 0) {
        this.date1 = e.value;
        // Save the date to the date component only if it has valid value
        if (e.value !== null && e.value !== "") {
          this.modelDate1 = e.value;
        }
      } else if (index === 1) {
        this.date2 = e.value;
        // Save the date to the date component only if it has valid value
        if (e.value !== null && e.value !== "") {
          this.modelDate2 = e.value;
        }
      }
    },
    initDates: function () {
      if (this.criteria === "") {
        this.date1 = "";
        this.date2 = "";
      } else {
        this.date1 =
          this.valueDate1 && this.valueDate1 !== ""
            ? this.valueDate1
            : this.dateString(new Date());
        if (this.criteria !== "between") {
          this.date2 = "";
        } else {
          this.date2 =
            this.valueDate2 && this.valueDate2 !== ""
              ? this.valueDate2
              : this.dateString(new Date());
        }
      }
      this.modelDate1 = this.date1;
      this.modelDate2 = this.date2;
    },
    onOpenMenu: function () {
      // Set the date range popup values
      this.errorsDate1 = [];
      this.errorsDate2 = [];
      this.criteria = this.valueCriteria || "";
      this.initDates();
    },
    onCloseMenu: function () {
      // Set the date component model since it is never getting set to empty
      // if the popup is cancelled or closed without update
      // Reset the cat-attribute-date data
      this.modelDate1 = this.date1;
      this.modelDate2 = this.date2;
    },
    onCancel: function (closeFn) {
      closeFn(null, true);
      this.$emit("ui-cancel");
    },
    onConfirm: function (closeFn) {
      if (!this.validate()) {
        return false;
      }

      // If not using custom criteria text, update the menu button label
      if (!this.customCriteriaText) {
        this.criteriaText = this.getCriteriaText(this.options, this.criteria);
      }

      // These are the dates that will be sent to the event
      if (this.criteria === "") {
        this.date1 = "";
        this.date2 = "";
      } else if (this.criteria !== "between") {
        this.date2 = "";
      }

      // Close the popup menu and notify
      closeFn(null, true);
      this.$emit("ui-update", {
        value: { criteria: this.criteria, dates: [this.date1, this.date2] }
      });
    },
    validate: function () {
      this.errorsDate1 = [];
      this.errorsDate2 = [];
      if (this.criteria !== "") {
        if (!this.isValidDate(this.date1)) {
          this.errorsDate1 = [
            {
              level: 1,
              msg: this.resolveTxt(
                "skin.dateRange.error.invalid.date",
                "Please enter a valid date"
              )
            }
          ];
          return false;
        }

        if (!this.validateDateMinMax(this.date1)) {
          this.errorsDate1 = [{ level: 1, msg: this.errorMsgMinMax }];
          return false;
        }

        if (this.criteria === "between") {
          if (!this.isValidDate(this.date2)) {
            this.errorsDate2 = [
              {
                level: 1,
                msg: this.resolveTxt(
                  "skin.dateRange.error.invalid.date",
                  "Please enter a valid date"
                )
              }
            ];
            return false;
          }

          if (!this.validateDateMinMax(this.date2)) {
            this.errorsDate2 = [{ level: 1, msg: this.errorMsgMinMax }];
            return false;
          }

          if (
            new Date(this.date1).getTime() >= new Date(this.date2).getTime()
          ) {
            this.errorsDate2 = [
              {
                level: 1,
                msg: this.resolveTxt(
                  "skin.dateRange.error.invalid.range",
                  "Please enter a valid date range"
                )
              }
            ];
            return false;
          }
        }
      }

      return true;
    },
    validateDateMinMax: function (dateString) {
      var minDate =
        this.minDate && this.minDate !== ""
          ? new Date(this.minDate).getTime()
          : 0;
      var maxDate =
        this.maxDate && this.maxDate !== ""
          ? new Date(this.maxDate).getTime()
          : 0;
      var date =
        dateString && dateString !== "" ? new Date(dateString).getTime() : 0;

      // Zero min and max means no limit set
      return !(
        (minDate !== 0 && date < minDate) ||
        (maxDate !== 0 && date > maxDate)
      );
    },
    isValidDate: function (dateString) {
      if (!dateString || dateString === "") {
        return false;
      }
      // Date string format is "YYYY-MM-DD"
      let parts = dateString.split("-");
      let year = parseInt(parts[0]);
      let month = parseInt(parts[1]);
      let day = parseInt(parts[2]);

      // Year and month ranges
      if (year < 1000 || month === 0 || month > 12) {
        return false;
      }

      var monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

      // Check for leap year
      if (year % 4000 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
        monthDays[1] = 29;
      }

      // Check if the day is valid for the month
      return day > 0 && day <= monthDays[month - 1];
    }
  }
});
</script>
